import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link as MuiLink,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import PasswordStrengthMeter from '../../components/Auth/PasswordStrengthMeter';
import { evaluatePassword } from '../../utils/passwordStrength';
import { getPortalToken, setPortalToken } from '../../lib/authToken';

type PortalStep = 'credentials' | 'password_change';

const PortalLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawNext = searchParams.get('next') || '/portal';
  const nextPath = rawNext.startsWith('/portal') ? rawNext : '/portal';
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const ssoError = searchParams.get('sso_error');
  const [error, setError] = useState(
    ssoError === 'staff_sso_only'
      ? 'Firm SSO is for staff only. Sign in here with your client portal credentials.'
      : ssoError
        ? `Sign-in failed: ${ssoError}`
        : ''
  );
  const [step, setStep] = useState<PortalStep>('credentials');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const strength = evaluatePassword(newPassword);

  const persistSession = (json: any) => {
    setPortalToken(json.data.token);
    localStorage.setItem('portalClient', JSON.stringify(json.data.client));
    localStorage.setItem('portalUser', JSON.stringify(json.data.user));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/portal/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Login failed');

      persistSession(json);

      if (json.data.user?.requiresPasswordChange || json.data.user?.isFirstLogin) {
        setStep('password_change');
        toast.success('Please set a permanent password to continue');
        return;
      }

      toast.success('Welcome to the client portal');
      navigate(nextPath);
    } catch (err: any) {
      setError(err.message || 'Login failed');
      toast.error(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!strength.valid) {
      setError(strength.errors.join('. ') || 'Password does not meet requirements');
      return;
    }

    setLoading(true);
    try {
      const token = getPortalToken();
      const res = await fetch('/api/portal/first-time-password-change', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ newPassword }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Unable to set password');

      const stored = JSON.parse(localStorage.getItem('portalUser') || '{}');
      localStorage.setItem(
        'portalUser',
        JSON.stringify({
          ...stored,
          isFirstLogin: false,
          requiresPasswordChange: false,
        })
      );

      toast.success('Password saved. Welcome to the client portal.');
      navigate(nextPath);
    } catch (err: any) {
      setError(err.message || 'Unable to set password');
      toast.error(err.message || 'Unable to set password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 420, width: '100%' }}>
        <Typography variant="h5" gutterBottom fontWeight={600}>
          Client Portal
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {step === 'password_change'
            ? 'Create your own password. You will use this for future sign-ins.'
            : 'Sign in to view your cases and documents.'}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {step === 'password_change' ? (
          <form onSubmit={handlePasswordChange}>
            <Alert severity="info" sx={{ mb: 2 }}>
              For security, replace the temporary password with one only you know.
            </Alert>
            <TextField
              fullWidth
              label="New password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNewPassword((v) => !v)} edge="end">
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <PasswordStrengthMeter password={newPassword} />
            <TextField
              fullWidth
              label="Confirm password"
              type={showNewPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
              error={Boolean(confirmPassword) && confirmPassword !== newPassword}
              helperText={
                confirmPassword && confirmPassword !== newPassword ? 'Passwords do not match' : ' '
              }
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !strength.valid || newPassword !== confirmPassword}
              sx={{ mt: 1, mb: 1 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Save password & continue'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((v) => !v)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ textAlign: 'right', mt: 0.5 }}>
              <MuiLink component={RouterLink} to="/portal/forgot-password" underline="hover" variant="body2">
                Forgot password?
              </MuiLink>
            </Box>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 2, mb: 1 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign in'}
            </Button>
          </form>
        )}
      </Paper>
    </Box>
  );
};

export default PortalLoginPage;
